import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { CurrencySymbol } from '@/components/ui/currency-symbol'
import { PayoutActions } from '@/components/financials/PayoutActions'
import type { PayoutStatement } from '@/types/financials'
import { financialsApi } from '@/api/financials'
import { useQuery } from '@tanstack/react-query'
import { format as formatDateFns } from 'date-fns'
import { CheckCircle, Clock, Download, Eye, FileDown, FileSpreadsheet, Loader2, XCircle } from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'

type PayoutSectionLabel = 'Pending Approval' | 'In Progress' | 'Processed' | 'Disapproved'

function formatDate(value?: string | null): string {
  if (!value) return 'N/A'
  try {
    return formatDateFns(new Date(value), 'MMM dd, yyyy')
  } catch {
    return 'N/A'
  }
}

function formatDateTime(value?: string | null): string {
  if (!value) return 'N/A'
  try {
    return formatDateFns(new Date(value), 'MMM dd, yyyy HH:mm')
  } catch {
    return 'N/A'
  }
}

/**
 * Map backend status to UI section label.
 * 
 * Pending Approval: pending_review
 * In Progress: approved, confirmed_by_wtd, confirmed_by_provider, invoiced, processing
 * Processed: paid
 * Disapproved: cancelled, disputed
 */
function sectionLabelForStatus(status?: string | null): PayoutSectionLabel {
  const s = (status || '').toLowerCase().replace(/-/g, '_')
  
  if (s === 'pending_review') return 'Pending Approval'
  
  if (s === 'cancelled' || s === 'disputed') return 'Disapproved'
  
  if (s === 'paid') return 'Processed'
  
  if (
    s === 'approved' || 
    s === 'confirmed_by_wtd' || 
    s === 'confirmed_by_provider' || 
    s === 'invoiced' || 
    s === 'processing' ||
    s === 'in_progress'
  ) return 'In Progress'
  
  return 'Pending Approval'
}

function getStatusLabelTranslation(label: PayoutSectionLabel, t: (key: string) => string): string {
  const map: Record<PayoutSectionLabel, string> = {
    'Pending Approval': 'financials.pendingApproval',
    'In Progress': 'financials.inProgress',
    Processed: 'financials.processed',
    Disapproved: 'financials.disapproved',
  }
  return t(map[label])
}

/**
 * Get the total amount for a payout statement.
 * Supports both new backend format (grossAmount) and legacy format.
 */
function getStatementTotal(statement: PayoutStatement): number {
  // Prefer grossAmount from new backend
  if (typeof statement.grossAmount === 'number') return statement.grossAmount
  // Fallback to totalAmount
  if (typeof statement.totalAmount === 'number') return statement.totalAmount
  // Legacy calculation
  return (statement.totalProviderCut || 0) + (statement.totalProviderVat || 0)
}

/**
 * Display name for the attached document.
 *
 * The backend sends `supporting_document_name`; the URL-derived guess is only a
 * fallback for legacy statements that have a URL but no name.
 */
function getInvoiceName(invoiceUrl: string | null): string | null {
  if (!invoiceUrl) return null
  try {
    const urlWithoutQuery = invoiceUrl.split('?')[0]
    const last = urlWithoutQuery.split('/').pop() || 'invoice'
    const decoded = decodeURIComponent(last)
    return decoded.length > 40 ? decoded.slice(0, 30) + '...' + decoded.slice(-8) : decoded
  } catch {
    return 'invoice'
  }
}

interface PayoutQuickDetailsDialogProps {
  statement: PayoutStatement | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onApprove: (statementId: string, document: File, notes?: string) => Promise<void>
  onDispute: (statementId: string, reason: string, document: File) => Promise<void>
  actionsDisabled?: boolean
}

export function PayoutQuickDetailsDialog({
  statement,
  open,
  onOpenChange,
  onApprove,
  onDispute,
  actionsDisabled = false,
}: PayoutQuickDetailsDialogProps) {
  const { t } = useLocalization()

  const detailQuery = useQuery({
    queryKey: ['payoutStatementDetails', statement?.id],
    queryFn: () => financialsApi.getPayoutStatementDetails(statement!.id),
    enabled: open && !!statement?.id,
    // supportingDocumentUrl is a signed, expiring link the backend regenerates
    // on every read, so this result must not be reused from cache.
    staleTime: 0,
    refetchOnMount: 'always',
  })

  if (!statement) return null

  const detail = detailQuery.data ?? statement
  const statusLabel = sectionLabelForStatus(detail.status)
  const total = getStatementTotal(detail)
  const payoutDetails = detail.payoutDetails?.length ? detail.payoutDetails : statement.payoutDetails
  const txCount = detail.payoutCount ?? payoutDetails.length
  // Only from `detail` (the fresh fetch) — the signed URL on the cached list
  // row would be stale. approvalInvoiceUrl is the legacy fallback.
  const invoiceUrl = detail.supportingDocumentUrl || detail.approvalInvoiceUrl || null
  const loadingDetails = detailQuery.isLoading

  const statusConfig: Record<PayoutSectionLabel, { className: string; Icon: any }> = {
    'Pending Approval': { className: 'bg-yellow-100 text-yellow-800', Icon: Clock },
    'In Progress': { className: 'bg-blue-100 text-blue-800', Icon: Clock },
    Processed: { className: 'bg-green-100 text-green-800', Icon: CheckCircle },
    Disapproved: { className: 'bg-red-100 text-red-800', Icon: XCircle },
  }

  const { className: statusClassName, Icon: StatusIcon } = statusConfig[statusLabel]

  const invoiceName = detail.supportingDocumentName || getInvoiceName(invoiceUrl)

  const exportRows = () => {
    const rows =
      Array.isArray(payoutDetails) && payoutDetails.length > 0
        ? payoutDetails
        : [
            {
              orderCustomId: '—',
              providerName: detail.providerName || '—',
              providerCut: detail.totalProviderCut,
              providerVat: detail.totalProviderVat,
            },
          ]

    return rows.map((d) => ({
      orderCustomId: d.orderCustomId || '—',
      providerName: d.providerName || detail.providerName || '—',
      amount: ('grossAmount' in d && d.grossAmount)
        ? Number(d.grossAmount)
        : (Number(d.providerCut || 0) + Number(d.providerVat || 0)),
      date: ('serviceDate' in d && d.serviceDate) || detail.periodEnd || detail.periodStart || null,
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] w-[95vw] sm:w-full overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="truncate text-sm sm:text-base">
                {t('financials.statementDetails', { id: statement.statementNumber || statement.id })}
              </span>
              <Badge variant="secondary" className={statusClassName}>
                <StatusIcon className="w-3 h-3 me-1" />
                {getStatusLabelTranslation(statusLabel, t)}
              </Badge>
            </div>

            {statusLabel === 'Pending Approval' && (
              <PayoutActions
                statementId={statement.id}
                onApprove={onApprove}
                onDispute={onDispute}
                disabled={actionsDisabled}
              />
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pe-2">
          {/* Summary */}
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('financials.totalAmount')}</TableHead>
                    <TableHead>{t('financials.transactions')}</TableHead>
                    <TableHead>{t('financials.period')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">
                      <CurrencySymbol amount={total} />
                    </TableCell>
                    <TableCell>{txCount}</TableCell>
                    <TableCell>
                      {formatDate(statement.periodStart)} → {formatDate(statement.periodEnd)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Invoice */}
          <div>
            <div className="text-sm font-semibold mb-2">{t('financials.supportingDocument')}</div>
            {invoiceUrl ? (
              <div className="rounded-md border border-green-200 bg-green-50 p-4">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                  <CheckCircle className="h-4 w-4 me-2" />
                  {t('financials.documentUploaded')}
                </h4>
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-green-800 flex-1 min-w-0">
                    <div className="font-medium truncate" title={invoiceUrl}>
                      {invoiceName}
                    </div>
                    <div className="text-green-700 text-xs">
                      {t('financials.uploaded')}: {formatDateTime(statement.periodEnd || statement.periodStart)}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <Button
                      asChild
                      variant="default"
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700"
                    >
                      <a href={invoiceUrl} target="_blank" rel="noopener noreferrer">
                        <Eye className="w-4 h-4 me-1" />
                        {t('common.view')}
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="border-green-600 text-green-700 hover:bg-green-600 hover:text-white"
                    >
                      <a href={invoiceUrl} download={invoiceName || 'document'}>
                        <Download className="w-4 h-4 me-1" />
                        {t('common.download')}
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            ) : statusLabel === 'Disapproved' ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <h4 className="font-semibold text-red-800 mb-1 flex items-center">
                  <XCircle className="h-4 w-4 me-2" />
                  {t('financials.statementDisputed')}
                </h4>
                <div className="text-red-700 text-sm">
                  {statement.disputeReason || statement.approvalNotes || t('financials.noNotesProvided')}
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                {t('financials.invoiceAvailableAfterApproval')}
              </div>
            )}
          </div>

          {/* Transactions */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
              <div className="text-sm font-semibold">{t('financials.transactionsSection')}</div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const rows = exportRows()
                    const header = [t('financials.orderCustomId'), t('financials.providerName'), t('financials.amount'), t('common.date')]
                    const data = rows.map((r) => [
                      r.orderCustomId,
                      r.providerName,
                      String(r.amount),
                      formatDate(r.date),
                    ])
                    const csv = [header, ...data]
                      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
                      .join('\n')
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `transactions-${statement.id}.csv`
                    a.click()
                    URL.revokeObjectURL(url)
                  }}
                >
                  <FileDown className="w-4 h-4 me-1" />
                  {t('financials.exportCsv')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const rows = exportRows()
                    const header = [t('financials.orderCustomId'), t('financials.providerName'), t('financials.amount'), t('common.date')]
                    const data = rows.map((r) => [
                      r.orderCustomId,
                      r.providerName,
                      String(r.amount),
                      formatDate(r.date),
                    ])
                    const tsv = [header, ...data].map((r) => r.join('\t')).join('\n')
                    const blob = new Blob([tsv], { type: 'application/vnd.ms-excel' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `transactions-${statement.id}.xls`
                    a.click()
                    URL.revokeObjectURL(url)
                  }}
                >
                  <FileSpreadsheet className="w-4 h-4 me-1" />
                  {t('financials.exportExcel')}
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('financials.orderCustomId')}</TableHead>
                      <TableHead>{t('financials.providerName')}</TableHead>
                      <TableHead>{t('financials.amount')}</TableHead>
                      <TableHead>{t('common.date')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingDetails ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-6">
                          <Loader2 className="w-5 h-5 animate-spin inline-block me-2" />
                          {t('common.loading')}
                        </TableCell>
                      </TableRow>
                    ) : Array.isArray(payoutDetails) && payoutDetails.length > 0 ? (
                      payoutDetails.map((d, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono">{d.orderCustomId || '—'}</TableCell>
                          <TableCell>{d.providerName || detail.providerName || '—'}</TableCell>
                          <TableCell>
                            <CurrencySymbol amount={d.grossAmount ?? (Number(d.providerCut || 0) + Number(d.providerVat || 0))} />
                          </TableCell>
                          <TableCell>{formatDate(d.serviceDate) || formatDate(detail.periodEnd || detail.periodStart)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell className="font-mono">—</TableCell>
                        <TableCell>{detail.providerName || '—'}</TableCell>
                        <TableCell>
                          <CurrencySymbol amount={total} />
                        </TableCell>
                        <TableCell>{formatDate(detail.periodEnd || detail.periodStart)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

